// ════════════════════════════════════════════════════════════════════════════
// PHASE 3 — CLIENT NETWORKING
// Connects to the relay server (server/relay.js) at the game's own origin by
// default, exposes window.NET for game.js to call into (role, send, on*
// hooks), and mirrors a set of dispatched actions into the opponent-side
// state of the other connected tab.
// Connection failure is silent — solo play must behave identically whether
// or not a relay is running.
// ════════════════════════════════════════════════════════════════════════════
(function () {
  let socket = null;
  // Set right before our own disconnect()/socket.close() so the close
  // listener can tell "we hung up on purpose" apart from an unexpected drop
  // (crashed relay, timed-out connection, etc.) — only the latter should
  // fire hooks.socketClosedUnexpectedly.
  let intentionalDisconnect = false;

  const badge = document.getElementById('net-status-badge');
  function setBadge(text) { if (badge) badge.textContent = text; }

  const hooks = {
    matchReady: [],
    opponentLeft: [],
    socketError: [],
    socketClosedUnexpectedly: [],
    mulliganDone: [],
    phaseSync: [],
    endTurn: [],
    surrender: [],
    counterSync: [],
    shieldReveal: [],
    shieldResolve: [],
    attackBlock: [],
    attackAction: [],
    unitBounce: [],
    unitToDeckBottom: [],
    forceShieldHit: [],
    forceDestroyNonBattling: [],
    offerDiscard: [],
    discardResponse: [],
    offerDraw: [],
    drawResponse: [],
    forceDiscard: [],
    specialMoveCommandActivated: [],
    fieldApMod: [],
    unitRest: [],
    destroyedEffectDone: [],
    attackTriggersDone: [],
    stuckCheck: [],
    stuckCheckResponse: [],
    requestMutualBattleUnit: [],
    mutualBattleUnitResponse: [],
    requestTrashUnitExile: [],
    trashUnitExileDone: [],
    endPhaseOppRequest: [],
    endPhaseAction: [],
    cardReveal: [],
    deckMill: [],
    // Private Games lobby (server/relay.js's LOBBY_* messages) — reset by
    // disconnect() below like matchReady, since the lobby page can be
    // entered/left/re-entered multiple times in one session and a stale
    // registration from a prior visit must not double-fire on a later one.
    lobbyCreated: [],
    lobbyJoined: [],
    lobbyState: [],
    lobbyError: [],
    challengeReceived: [],
    challengeSent: [],
    challengeDeclined: [],
    challengeTimeout: [],
    challengeAccepted: [],
    matchAborted: [],
    lobbyClosed: [],
    lobbyKicked: [],
    // Best of 3 in-match signals — deliberately NOT reset on disconnect()
    // like the lobby hooks above, since these fire mid-series (the socket
    // stays connected across all games of a Bo3 match) and are registered
    // exactly once, permanently, in game.js, same treatment as
    // socketError/socketClosedUnexpectedly.
    bo3SideboardReady: [],
    bo3TurnChoice: [],
  };

  // MATCH_READY can arrive before showMatchmakingScreen() has registered its
  // onMatchReady callback (e.g. both tabs connect/match almost instantly,
  // well before the player has clicked through deck-select). Latch it so a
  // late registration still fires instead of waiting on an event that
  // already happened.
  let matchReadyFired = false;

  // Applies a peer's already-resolved action to my opponent-side state.
  const remoteHandlers = {
    // One wire carries both a fresh placement (PLAY_CARD) and a pilot paired
    // onto an already-deployed unit (PAIR_PILOT) — see setActionListener's
    // single `type === 'PLAY_CARD' || type === 'PAIR_PILOT'` branch below,
    // which always sends type 'PLAY_CARD'. Distinguished here by whether
    // this slot was already occupied: this used to render and sync state
    // (so the opponent's board stays correct either way) but never called
    // addLog() at all, so the peer's own match log silently had no line for
    // anything the OPPONENT played — only the player who actually played a
    // card ever saw it logged, on their own client.
    PLAY_CARD({ slot, card, counters }) {
      console.log(`[Opponent] Played ${card.name}`);
      playSfx('card_played');
      const prevCard = state.oppField[slot];
      state.oppField[slot] = card;
      applyOppCounters(counters);
      if (prevCard && card.pilot) {
        addLog(isLinkUnit(card)
          ? `${card.pilot.name} <span class="material-symbols-outlined log-link-icon">link</span> as pilot to ${card.name}.`
          : `${card.pilot.name} paired as pilot to ${card.name}.`);
      } else {
        addLog(`Played ${card.name}.`);
      }
      render();
      setupDropZones();
    },
    ATTACK(payload) {
      dispatch('ATTACK', { ...payload, mine: false });
    },
    // Field reposition (drag a field card onto another field slot) — was
    // previously unmirrored entirely, letting the opponent's oppField view
    // of my field silently drift out of sync with reality.
    SWAP_SLOTS({ srcSlot, tgtSlot }) {
      const card      = state.oppField[srcSlot];
      const displaced = state.oppField[tgtSlot];
      state.oppField[tgtSlot] = card;
      state.oppField[srcSlot] = displaced;
      render();
      setupDropZones();
    },
    // A field card discarded (dragged to the trash zone) — hand discards
    // aren't mirrored (no opponent-visible representation exists for hand
    // contents), only field discards, which the opponent can see.
    DISCARD_CARD({ slot }) {
      state.oppField[slot] = null;
      render();
      setupDropZones();
    },
    DEPLOY_BASE({ card, counters }) {
      hideDecisionWaitingOverlay();
      const prevBase = state.oppBase;
      state.oppBase = card;
      applyOppCounters(counters);
      addLog(prevBase ? `${card.name} replaced ${prevBase.name} as base.` : `${card.name} set as base.`);
      render();
      setupDropZones();
    },
    // Log-only mirror of the peer's own dispatch('NEW_TURN') — see the
    // 'NEW_TURN' branch in setActionListener below for why this is needed at
    // all. Never touches state.turn; that's kept in sync by PHASE_SYNC/
    // END_TURN independently of this purely-cosmetic log line.
    NEW_TURN({ turn }) {
      addLog(`─── Turn ${turn} begins ───`, { turnMarker: true });
    },
    // Peer's Card Appearance picks (Edit Profile) — sent once, right at
    // match start (see initGame() in game.js), so my mirrored view of their
    // Resource/EX Resource tray and their starting EX Base show what THEY
    // actually chose instead of my own choice or the standard art.
    // Rebuilding state.oppBase here unconditionally is safe: this always
    // arrives well before either side could take a real game action (a
    // later genuine DEPLOY_BASE, if any, simply overwrites it again).
    CARD_APPEARANCE({ appearance }) {
      if (!appearance || typeof appearance !== 'object') return;
      state.oppCardAppearance = {
        resource:   typeof appearance.resource   === 'string' ? appearance.resource   : null,
        exBase:     typeof appearance.exBase     === 'string' ? appearance.exBase     : null,
        exResource: typeof appearance.exResource === 'string' ? appearance.exResource : null,
      };
      if (state.oppCardAppearance.exBase) {
        state.oppBase = makeBaseCardFromEntry(getOppCardAppearanceEntry('exBase'), 'EXB');
      }
      render();
    },
    ACTIVATE_BASE({ counters, rested } = {}) {
      const base = state.oppBase;
      if (base) {
        base.rested = !!rested;
        showOpponentPlayModal(base, `Opponent activated ${base.name}'s effect.`);
      }
      applyOppCounters(counters);
      render();
    },
    // Mirrors a card whose owner exiled it from their own trash (e.g. paying
    // GD04-130 Industrial 7's activation cost) — only they have authoritative
    // trash data, so this just tells us which uid to move off our oppTrash
    // mirror and into oppExile. Falls back to the `card` sent alongside `uid`
    // when our oppTrash mirror doesn't have a matching entry (e.g. an earlier
    // trash-add for it never reached us) — without this, that exile was
    // silently dropped instead of landing in oppExile, undercounting the
    // opponent's EXILED total (e.g. exiling 3 cards only showing 2).
    TRASH_EXILE({ uid, card }) {
      const idx = state.oppTrash.findIndex(c => c.uid === uid);
      if (idx !== -1) {
        const [existing] = state.oppTrash.splice(idx, 1);
        state.oppExile.push(existing);
      } else if (card) {
        state.oppExile.push(card);
      }
      render();
    },
    // Mirrors a card whose owner retrieved it from their own trash back to
    // their hand (e.g. GD03-091 Rau Le Creuset) — only they have authoritative
    // trash data, so this just tells us which uid to drop from our oppTrash
    // mirror; their oppHandCount catches up via the next COUNTER_SYNC.
    TRASH_TO_HAND({ uid }) {
      const idx = state.oppTrash.findIndex(c => c.uid === uid);
      if (idx !== -1) state.oppTrash.splice(idx, 1);
      render();
    },
    CHANGE_ATTACK_TARGET({ uid }) {
      const unit = [...state.playerField, ...state.oppField].find(c => c && c.uid === uid);
      state.pendingDefenderOverride = unit || null;
    },
    CLEAR_TURN_EFFECTS() {
      // Keep this field list in sync with registerAction('CLEAR_TURN_EFFECTS', ...)
      // in game.js — this is a separate reimplementation (mirrors onto
      // oppField/playerField instead of dispatch()'s own playerField/oppField,
      // to avoid re-triggering the network mirror send) that drifted out of
      // sync with it before (missing tempIncomingDamageReduction among others
      // — e.g. GD05-018 Gundam Calibarn's "reduce enemy damage by 3 this turn"
      // grant never expired on the granting player's OPPONENT's client, since
      // only the sender ever ran the up-to-date game.js version).
      const clear = c => {
        if (!c) return;
        c.tempKeywords = [];
        c.tempApMod = 0;
        c.battleApMod = 0;
        c.battleDefMod = 0;
        c.tempDestroyOnHit = null;
        c.tempCantAttack = false;
        c.tempCantReceiveEffectDamage = false;
        c.tempCantAttackNonUnit = false;
        c.tempCantDirectAttack = false;
        c.tempCantBlock = false;
        c.tempImmuneToEffectDestruction = false;
        c.linkedThisTurn = false;
        c.nextDamageReduction = 0;
        c.tempGrantedAbilities = [];
        c.tempAttackDespiteSickness = false;
        c.tempIncomingDamageReduction = 0;
        c.tempBattleDamageReduction = 0;
      };
      // Clear both fields: oppField mirrors the sender's playerField (our
      // opponent's units); playerField holds any debuffs the opponent applied
      // to our units during their Action Step.
      state.oppField.forEach(clear);
      state.playerField.forEach(clear);
      render();
    },
    UNREST_ALL() {
      // Keep this field list in sync with registerAction('UNREST_ALL', ...) in
      // game.js — see the CLEAR_TURN_EFFECTS comment above for why this is a
      // separate reimplementation and how it drifted out of sync before.
      state.oppField.forEach(c => {
        if (c) {
          if (!c.keepRestedNextTurn) c.rested = false; c.keepRestedNextTurn = false;
          c.tempKeywords = []; c.tempApMod = 0; c.battleApMod = 0;
          c.tempCantAttack = false; c.tempCantReceiveEffectDamage = false; c.tempCantAttackNonUnit = false;
          c.tempCantDirectAttack = false;
          c.tempCantBlock = false;
          c.tempImmuneToEffectDestruction = false;
          c.battleDefMod = 0; c.tempDestroyOnHit = null; c.nextDamageReduction = 0;
          c.tempGrantedAbilities = [];
          c.tempAttackDespiteSickness = false;
          c.tempIncomingDamageReduction = 0;
          c.tempBattleDamageReduction = 0;
          c.passiveUsedThisTurn = false; c.activateMainUsedThisTurn = false; c.activateActionUsedThisTurn = false;
          if (c.pilot) { c.pilot.passiveUsedThisTurn = false; c.pilot.activateMainUsedThisTurn = false; c.pilot.pilotEffectUsedThisTurn = false; }
        }
      });
      if (state.oppBase) {
        state.oppBase.rested = false;
        state.oppBase.baseAbilityUsedThisTurn = false;
        state.oppBase.baseActionUsedThisTurn = false;
        state.oppBase.passiveUsedThisTurn = false;
      }
      state.forcedAttackTargetUid = null;
      state.attackerBdProxyUid = null;
      render();
    },
    MATCH_READY() {
      matchReadyFired = true;
      console.log(`[network] MATCH_READY — ${hooks.matchReady.length} listener(s) registered so far`);
      hooks.matchReady.forEach(cb => cb());
    },
    OPPONENT_LEFT() {
      hooks.opponentLeft.forEach(cb => cb());
    },
    // Private Games lobby (server/relay.js) — every handler just forwards
    // the payload to its hook, same one-liner style as COUNTER_SYNC/
    // SHIELD_REVEAL above. ROLE/MATCH_READY need no lobby-specific handling
    // at all — pairAndStart() on the server sends the exact same messages
    // regardless of whether the match came from the public queue or a
    // lobby challenge, so the handlers above already cover it.
    LOBBY_CREATED({ code, selfId }) {
      hooks.lobbyCreated.forEach(cb => cb({ code, selfId }));
    },
    LOBBY_JOINED({ code, selfId }) {
      hooks.lobbyJoined.forEach(cb => cb({ code, selfId }));
    },
    LOBBY_STATE({ code, members, settings }) {
      hooks.lobbyState.forEach(cb => cb({ code, members, settings }));
    },
    LOBBY_ERROR({ message }) {
      hooks.lobbyError.forEach(cb => cb(message));
    },
    LOBBY_CHALLENGE_RECEIVED({ fromId, fromUsername }) {
      hooks.challengeReceived.forEach(cb => cb({ fromId, fromUsername }));
    },
    LOBBY_CHALLENGE_SENT({ toId, toUsername }) {
      hooks.challengeSent.forEach(cb => cb({ toId, toUsername }));
    },
    LOBBY_CHALLENGE_DECLINED({ byUsername }) {
      hooks.challengeDeclined.forEach(cb => cb(byUsername));
    },
    LOBBY_CHALLENGE_TIMEOUT() {
      hooks.challengeTimeout.forEach(cb => cb());
    },
    LOBBY_CHALLENGE_ACCEPTED({ opponentId, opponentUsername }) {
      hooks.challengeAccepted.forEach(cb => cb({ opponentId, opponentUsername }));
    },
    LOBBY_MATCH_ABORTED({ reason }) {
      hooks.matchAborted.forEach(cb => cb(reason));
    },
    // Sent when the lobby's creator's connection ends for any reason (the
    // explicit Close Lobby button, quitting their own match, or just
    // leaving/closing the tab) — every remaining member gets this, whether
    // they were idly sitting in the lobby or independently trying to
    // LOBBY_RETURN from their own just-finished match.
    LOBBY_CLOSED({ reason }) {
      hooks.lobbyClosed.forEach(cb => cb(reason));
    },
    // Sent only to the one member the lobby's creator just removed via the
    // Remove Player button (see LOBBY_KICK in server/relay.js) — unlike
    // LOBBY_CLOSED this never reaches anyone else, since the lobby itself
    // keeps running for the remaining members.
    LOBBY_KICKED() {
      hooks.lobbyKicked.forEach(cb => cb());
    },
    BO3_SIDEBOARD_READY() {
      hooks.bo3SideboardReady.forEach(cb => cb());
    },
    BO3_TURN_CHOICE(payload) {
      hooks.bo3TurnChoice.forEach(cb => cb(payload));
    },
    MULLIGAN_DONE() {
      hooks.mulliganDone.forEach(cb => cb());
    },
    PHASE_SYNC(payload) {
      hooks.phaseSync.forEach(cb => cb(payload));
    },
    END_TURN() {
      hooks.endTurn.forEach(cb => cb());
    },
    SURRENDER() {
      hooks.surrender.forEach(cb => cb());
    },
    COUNTER_SYNC({ counters }) {
      hooks.counterSync.forEach(cb => cb(counters));
    },
    SHIELD_REVEAL({ cards, hasBursts }) {
      hooks.shieldReveal.forEach(cb => cb(cards, hasBursts));
    },
    SHIELD_RESOLVE({ card }) {
      hooks.shieldResolve.forEach(cb => cb(card));
    },
    // Sent by an effect that privately looks at a card (e.g. the top of the
    // deck) and decides to reveal it to the opponent (e.g. ST06-009 adding a
    // (Clan) card to hand) — the peer doesn't need to apply any state here,
    // just inform the local player it happened.
    CARD_REVEAL({ card, pilotName }) {
      hooks.cardReveal.forEach(cb => cb(card, pilotName));
    },
    // Sent when an effect unconditionally mills the top card of the player's
    // own deck straight into their trash (e.g. GD03-092 Nyaan) — unlike a
    // private peek, this card is always public, so the peer needs the full
    // card identity to mirror it into their oppTrash view (mirrors
    // SHIELD_RESOLVE's purpose for shield-originated trash cards).
    DECK_MILL({ card }) {
      hooks.deckMill.forEach(cb => cb(card));
    },
    ATTACK_BLOCK({ blockerUid }) {
      hooks.attackBlock.forEach(cb => cb(blockerUid));
    },
    ATTACK_ACTION({ side, played }) {
      hooks.attackAction.forEach(cb => cb(side, played));
    },
    // Sent directly by a card effect (e.g. GD03-118) that returns one of MY
    // real units to MY hand — only I have the authoritative hand data, so
    // the opponent's client just tells me which uid to pull off my own
    // playerField, not what the resulting hand card looks like.
    UNIT_BOUNCE({ uid }) {
      hooks.unitBounce.forEach(cb => cb(uid));
    },
    // Sent directly by a card effect (e.g. GD05-002 Strike Freedom Gundam)
    // that returns one of MY real units to the bottom of MY own deck — only
    // I have the authoritative tempDeck, so the opponent's client just tells
    // me which uid to pull off my own playerField.
    UNIT_TO_DECK_BOTTOM({ uid }) {
      hooks.unitToDeckBottom.forEach(cb => cb(uid));
    },
    // Sent by a non-combat effect (e.g. GD05-107 Interwoven Blessings) that
    // destroys N of the OPPONENT's shield-zone cards simultaneously (like
    // native Suppression, but outside of battle). Only the shield owner's own
    // client has the authoritative private shield identities, so this just
    // tells them to resolve `count` of their own shields exactly the way
    // resolveMyShieldHit() already does for combat.
    FORCE_SHIELD_HIT({ count }) {
      hooks.forceShieldHit.forEach(cb => cb(count));
    },
    // Sent by a card effect (e.g. GD05-049 Sazabi) that forces the OPPONENT
    // to choose and destroy one of their own non-battling Units — only the
    // real owner has authoritative field/effect data for their own Units, so
    // this just tells them which uid to exclude (the Unit currently
    // battling) and lets them resolve the picker + destroy locally.
    FORCE_DESTROY_NON_BATTLING({ excludeUid }) {
      hooks.forceDestroyNonBattling.forEach(cb => cb(excludeUid));
    },
    // Sent by a card effect (e.g. GD05-034 Gaia Gundam: "the enemy player MAY
    // discard 1") that offers the OPPONENT an optional 1-card discard from
    // their own hand — only they have authoritative hand data, so this just
    // tells them to resolve the picker locally. Their client replies with
    // DISCARD_RESPONSE once resolved, since some effects branch on whether
    // the discard actually happened.
    OFFER_DISCARD() {
      hooks.offerDiscard.forEach(cb => cb());
    },
    // Sent back by the OFFER_DISCARD recipient once they've resolved their
    // optional discard, so the original sender (whose effect may branch on
    // "if they don't discard...") knows the outcome. See game.js's
    // pendingDiscardResolve (mirrors pendingBlockResolve's single-callback
    // convention) for how the sender consumes this.
    DISCARD_RESPONSE({ discarded }) {
      hooks.discardResponse.forEach(cb => cb(discarded));
    },
    // EB01-062 Jona Basta's [Attack] — the activating player is offering ME
    // an optional draw from MY OWN deck (only I have authoritative deck
    // data). Same round-trip shape as OFFER_DISCARD/DISCARD_RESPONSE just
    // above, for a draw instead of a discard.
    OFFER_DRAW() {
      hooks.offerDraw.forEach(cb => cb());
    },
    DRAW_RESPONSE({ drew }) {
      hooks.drawResponse.forEach(cb => cb(drew));
    },
    // Sent by a card effect (e.g. GD05-046 Abyss Gundam (MA Mode)) that forces
    // the OPPONENT to discard 1 from their own hand — mandatory, no response
    // expected since no effect here branches on the outcome.
    FORCE_DISCARD() {
      hooks.forceDiscard.forEach(cb => cb());
    },
    // Sent whenever a (Special Move) Command's [Main]/[Action] is activated
    // (see triggerCardEffect in game.js), so a passiveKeywords-style live
    // check on the OPPONENT's own copy of a card (e.g. GD05-068 Shining
    // Gundam (Super Mode)) reads correctly on both clients.
    SPECIAL_MOVE_COMMAND_ACTIVATED() {
      hooks.specialMoveCommandActivated.forEach(cb => cb());
    },
    // Sent directly by a card effect (e.g. GD03-119) that modifies AP on
    // either side's units — only the real controller of those units has
    // authoritative card objects, so this just tells them which uids/delta/
    // field to apply on their own playerField. `field` is 'tempApMod'
    // ("during this turn") or 'battleApMod' ("during this battle").
    FIELD_AP_MOD({ uids, delta, field }) {
      hooks.fieldApMod.forEach(cb => cb(uids, delta, field));
    },
    // Sent by a card effect that restores HP on a unit (e.g. GD01-098 Elan
    // Ceres). Applied to whichever side's field contains the uid — both
    // clients must have the same HP for runDamageStep to stay in sync.
    FIELD_HP_MOD({ uids, delta }) {
      [...state.playerField, ...state.oppField].forEach(c => {
        if (c && uids.includes(c.uid)) {
          c.hp = (parseInt(c.hp) || 0) + delta;
          if (delta < 0) c.damaged = true;
          // Repair heal: clear damaged flag when restored to full HP.
          if (delta > 0 && effectiveHp(c) >= effectiveMaxHp(c)) c.damaged = false;
        }
      });
      render();
      // Animate then destroy units whose HP hit 0 from effect damage.
      // playerField = owned by this client (full trash + trigger handling).
      // oppField = mirror (null slot + approximate trash count only).
      const toDestroy = [];
      const survivors = [];
      state.playerField.forEach((c, i) => { if (c && uids.includes(c.uid)) (effectiveHp(c) <= 0 ? toDestroy : survivors).push({ c, i, side: 'player' }); });
      state.oppField.forEach((c, i)    => { if (c && uids.includes(c.uid)) (effectiveHp(c) <= 0 ? toDestroy : survivors).push({ c, i, side: 'opp' }); });
      // Damaged-but-surviving units still vibrate, even though they aren't destroyed.
      if (delta < 0) survivors.forEach(({ c, side }) => vibrateCard(side, c));
      if (toDestroy.length === 0) return;
      let settled = 0;
      toDestroy.forEach(({ c, i, side }) => {
        const cardEl = vibrateCard(side, c);
        pendingEffectDestroys++;
        const doDestroy = () => {
          state[`${side}Field`][i] = null;
          if (side === 'player') {
            const isToken = isUnitToken(c);
            if (!isToken) {
              updateTempState(c.cardNo, c.copyVer, 'trash');
              state.trashCount++;
              state.playerTrash.push(toTrashCard(c));
            }
            addLog(`${c.name} is destroyed by effect damage!${isToken ? ' (token — removed from the game)' : ''}`);
            // pilotToTrash is staged (not committed) until after the
            // 'destroyed' triggers below, so a handler like GD01-005
            // Unicorn Gundam (Unicorn Mode)'s "[During Link][Destroyed]
            // Return this Unit's paired Pilot to its owner's hand" can
            // rescue it via ctx.rescuePilot() before it's trashed — same
            // convention as dealDamageToUnit's doDestroy (game.js). Without
            // this the pilot got trashed unconditionally here, then ALSO
            // pushed into hand by the rescuing handler, duplicating it.
            let pilotToTrash = c.pilot || null;
            const rescuePilot = () => { pilotToTrash = null; };
            triggerCardEffect(c, 'destroyed', { ownerSide: 'player', rescuePilot });
            if (c.pilot) triggerCardEffect(c.pilot, 'destroyed', { ownerSide: 'player', unit: c, rescuePilot });
            notifyUnitDestroyedByEffect(c);
            if (pilotToTrash) {
              updateTempState(pilotToTrash.cardNo, pilotToTrash.copyVer, 'trash');
              state.trashCount++;
              state.playerTrash.push(toTrashCard(pilotToTrash));
            }
          } else {
            if (!isUnitToken(c)) state.oppTrashCount++;
            if (c.pilot) state.oppTrashCount++;
          }
          if (++settled === toDestroy.length) { broadcastCounters(); render(); setupDropZones(); }
          pendingEffectDestroys--;
        };
        if (cardEl) {
          setTimeout(doDestroy, 500);
        } else {
          doDestroy();
        }
      });
    },
    // Sent by dealDamageToBase() (game.js) whenever a card effect changes a
    // Base's HP — mirrors onto whichever side's Base actually has this uid
    // (checked against both playerBase and oppBase, uid-agnostic exactly like
    // FIELD_HP_MOD above) and independently re-derives destruction from the
    // resulting HP. Previously dealDamageToBase sent nothing at all, so
    // effect damage to a Base (as opposed to battle damage, which both
    // clients already resolve identically on their own) never reached the
    // peer — their mirror of that Base silently stayed undamaged/alive.
    BASE_HP_MOD({ uid, delta }) {
      const side = state.playerBase?.uid === uid ? 'player' : state.oppBase?.uid === uid ? 'opp' : null;
      if (!side) return;
      const base = state[`${side}Base`];
      base.hp = (parseInt(base.hp) || 0) + delta;
      if (delta < 0) base.damaged = true;
      if (base.hp > 0) {
        render();
        if (delta < 0) requestAnimationFrame(() => vibrateCard(side, base));
        return;
      }
      const exBase = isExBaseCard(base);
      state[`${side}Base`] = null;
      if (!exBase) {
        state[trashCountKey(side)] += 1;
        state[trashArrayKey(side)].push(toTrashCard(base));
      }
      addLog(`${base.name} is destroyed!${exBase ? ' (EX Base — removed from game)' : ''}`);
      // Only ever resolve OUR OWN Base's destroyed effect locally — same
      // owner-only convention as FIELD_HP_MOD's unit-destroy branch above;
      // an enemy Base's destroyed trigger is that player's own client's
      // responsibility to resolve, not ours to replay from a bare HP mirror.
      if (side === 'player') triggerCardEffect(base, 'destroyed', { ownerSide: 'player' });
      broadcastCounters();
      render();
      setupDropZones();
    },
    // Sent directly by a card effect that rests/un-rests a Unit outside the
    // normal combat-driven ATTACK flow (e.g. setting a Unit active again) —
    // mirrors onto whichever client actually owns that uid on their
    // playerField.
    UNIT_REST({ uid, rested }) {
      hooks.unitRest.forEach(cb => cb(uid, rested));
    },
    ACTIVATE_COMMAND({ card, counters }) {
      console.log(`[Opponent] Activated ${card.name} (Command)`);
      state.oppTrash.push(toTrashCard(card));
      applyOppCounters(counters);
      addLog(`Opponent activated ${card.name}'s effect (sent to trash).`);
      showOpponentPlayModal(card, `Opponent played ${card.name}'s effect.`);
      render();
    },
    DESTROYED_EFFECT_DONE() {
      hooks.destroyedEffectDone.forEach(cb => cb());
    },
    ATTACK_TRIGGERS_DONE() {
      hooks.attackTriggersDone.forEach(cb => cb());
    },
    // Stuck-wait watchdog (see armStuckWaitWatchdog in game.js) — a "are you
    // still actually deciding something?" ping sent by a client that's been
    // stuck on a "Waiting on opponent's ___…" overlay for a while, in case
    // the message it's actually waiting on was lost or never sent (network
    // hiccup, or an undiscovered ordering bug).
    STUCK_CHECK() {
      hooks.stuckCheck.forEach(cb => cb());
    },
    STUCK_CHECK_RESPONSE({ busy }) {
      hooks.stuckCheckResponse.forEach(cb => cb(busy));
    },
    // ST12-013 The Final Victor's [Main] — the activating player already
    // chose their own Unit and is asking me to choose one of MY OWN Units
    // (only I have authoritative field data for it); MUTUAL_BATTLE_UNIT_RESPONSE
    // sends my choice (or null if I have no Units) back.
    REQUEST_MUTUAL_BATTLE_UNIT() {
      hooks.requestMutualBattleUnit.forEach(cb => cb());
    },
    MUTUAL_BATTLE_UNIT_RESPONSE({ uid }) {
      hooks.mutualBattleUnitResponse.forEach(cb => cb(uid));
    },
    // ST13-009 Gundam Pharact / ST14-003 Palace Athene's [Attack]/[Deploy] —
    // the activating player chose me as the enemy player and is asking me to
    // choose 2 Unit cards from MY OWN trash to exile (only I have
    // authoritative trash data for it); the actual exiling reuses the
    // existing exileFromTrash()/exileCardsFromTrash() helpers, which already
    // send their own TRASH_EXILE per card — TRASH_UNIT_EXILE_DONE is just a
    // lightweight "I'm finished choosing" acknowledgment so the requester's
    // wait can resolve.
    REQUEST_TRASH_UNIT_EXILE({ count = 2 } = {}) {
      hooks.requestTrashUnitExile.forEach(cb => cb(count));
    },
    TRASH_UNIT_EXILE_DONE() {
      hooks.trashUnitExileDone.forEach(cb => cb());
    },
    // Sent when a card effect forces the opponent to draw (e.g. GD04-128 Armory One).
    FORCE_DRAW({ count = 1 } = {}) {
      const cards = drawCardsFromTempDeck(count);
      cards.forEach(c => {
        state.hand.push(c);
        addLog(`Forced draw (${count > 1 ? count : 'opponent'} effect): ${c.name}.`);
      });
      if (cards.length === 0) addLog('Forced draw — deck is empty.');
      broadcastCounters();
      render();
    },
    // Sent when a card effect forces the opponent to deploy a Unit token to
    // THEIR OWN field (e.g. GD06-115 Teaching New Techniques: "The destroyed
    // Unit's owner deploys 1 [Nu Gundam] Unit token") — only the receiving
    // client can place it on their own authoritative field (and prompt them
    // to make room if it's full), so this just tells them which token to
    // deploy. Mandatory, no response expected — mirrors FORCE_DISCARD/
    // FORCE_DRAW's one-way convention.
    FORCE_DEPLOY_TOKEN({ cardNo }) {
      deployTokenToField(cardNo);
    },
    // Sent when a card effect makes both players place 1 EX Resource (e.g.
    // EB01-025 Tallgeese II's "All players place 1 EX Resource" bullet).
    // placeExResource() operates on the receiving client's own state.exRes.
    FORCE_PLACE_EX_RESOURCE() {
      placeExResource();
    },
    // Sent when a card effect makes both players each set 1 rested Resource
    // as active (e.g. EB01-059 Psycho Zaku's "All players each choose 1 of
    // their Resources. Set them as active."). Operates on the receiving
    // client's own state.res, capped at resBase.
    FORCE_SET_RESOURCE_ACTIVE() {
      state.res = Math.min(state.res + 1, state.resBase);
      broadcastCounters();
      render();
    },
    // Sent when a card effect makes both players independently resolve a
    // look-at-top-card-of-own-deck decision (e.g. EB01-078 Premium Unit
    // Assembly, EB01-023 Le Cygne). Runs the identical resolution used by
    // the activating client, but against the receiving client's own
    // tempDeck/hand. `mode` selects which qualifying condition applies.
    FORCE_LOOK_TOP_CARD_OR_RETURN({ cardName, mode } = {}) {
      const qualifies = mode === 'lv5'
        ? (peeked) => (parseInt(peeked.level) || 0) >= 5
        : (peeked) => peeked.type === 'unit';
      const qualifyLabel = mode === 'lv5' ? 'Lv.5 or higher card' : 'Unit';
      resolveLookTopCardOrReturn(cardName || 'Effect', () => {}, qualifies, qualifyLabel);
    },
    // Current turn player signals the opponent that it's the opponent's turn
    // to take an End Phase action step (opponent acts first per the rules).
    END_PHASE_OPP_REQUEST() {
      hooks.endPhaseOppRequest.forEach(cb => cb());
    },
    // Either side sends this when their end phase action step resolves — the
    // payload mirrors ATTACK_ACTION's shape so applyPeerActionPlay can handle it.
    END_PHASE_ACTION({ played }) {
      hooks.endPhaseAction.forEach(cb => cb(played));
    },
    TEMP_KEYWORD_ADD({ uid, keyword }) {
      const card = state.oppField.find(c => c && c.uid === uid);
      if (card) {
        card.tempKeywords = [...(card.tempKeywords || []), keyword];
        render();
      }
    },
    // GD04-107 Destined Battle: all enemies must attack the chosen rested unit this turn.
    // Sent by the defending player's client; the attacker applies it before setupDropZones.
    FORCE_ATTACK_TARGET({ uid }) {
      state.forcedAttackTargetUid = uid;
      render();
      setupDropZones();
    },
    // GD04-087 Elan Ceres: counter-damage to the attacker is redirected to this proxy unit.
    // Sent by the attacker's client from the pilot attack trigger so both clients agree.
    SET_ATTACKER_PROXY({ uid }) {
      state.attackerBdProxyUid = uid;
    },
    // Ali al-Saachez (GD04-099) [During Link][Attack]: the opponent removed a
    // pilot from one of our units and returned it to our hand. Move the pilot
    // off the unit in playerField, rebuild the card from DB, and push to hand.
    PILOT_UNPAIR({ unitUid, pilotUid }) {
      const unit = state.playerField.find(c => c && (c.uid === unitUid || c.pilot?.uid === pilotUid));
      if (!unit || !unit.pilot) return;
      const pilot = unit.pilot;
      unit.pilot = null;
      unit.pilotKeywords = []; // clear any passively-granted keywords (e.g. Cagalli Blocker)
      const dbEntry = cardDb[pilot.cardNo];
      if (dbEntry) {
        state.hand.push(buildGameCardFromDb(dbEntry, pilot.copyVer));
      }
      if (state.tempDeck) {
        const entry = state.tempDeck.find(e => e.cardNo === pilot.cardNo && e.copyVer === pilot.copyVer);
        if (entry) entry.state = 'hand';
      }
      broadcastCounters();
      render();
      setupDropZones();
    },
    // GD03-110 Eliminate Target: opponent effect destroyed a pilot linked to
    // one of our units. Strip the pilot, send it to our trash, fire destroyed.
    PILOT_DESTROY({ unitUid }) {
      const unit = state.playerField.find(c => c && c.uid === unitUid);
      if (!unit || !unit.pilot) return;
      const pilot = unit.pilot;
      unit.pilot = null;
      unit.pilotKeywords = [];
      updateTempState(pilot.cardNo, pilot.copyVer, 'trash');
      state.trashCount++;
      state.playerTrash.push(toTrashCard(pilot));
      triggerCardEffect(pilot, 'destroyed', { ownerSide: 'player', unit });
      broadcastCounters();
      render();
      setupDropZones();
    },
    // Ali al-Saachez (GD04-099): the opponent destroyed one of our units
    // because removing its pilot dropped its effective HP to 0. Move it from
    // playerField to playerTrash.
    OPP_UNIT_DESTROYED({ unitUid }) {
      const slot = state.playerField.findIndex(c => c && c.uid === unitUid);
      if (slot === -1) return;
      const unit = state.playerField[slot];
      state.playerField[slot] = null;
      state.playerTrash.push(toTrashCard(unit));
      if (state.tempDeck) {
        const entry = state.tempDeck.find(e => e.cardNo === unit.cardNo && e.copyVer === unit.copyVer);
        if (entry) entry.state = 'trash';
      }
      broadcastCounters();
      render();
      setupDropZones();
    },
    PICKER_OPEN({ card }) {
      showDecisionWaitingOverlay('Waiting for opponent to make a selection…', card || null);
    },
    PICKER_DONE() {
      hideDecisionWaitingOverlay();
    },
    // Post-combat authoritative HP sync sent by the attacker after runEpilogue.
    // Applies hp/damaged to all matching units, and destroys any playerField
    // unit the attacker's own combat resolution actually destroyed this
    // battle (missed locally due to HP desync between the two independently-
    // computed clients).
    //
    // destroyedUids is an explicit list, NOT inferred from a unit's absence
    // from `units` — that was the previous (buggy) design: `units` is built
    // from the attacker's own field snapshot, which only reflects units the
    // attacker's client currently knows about. Any OTHER unit that simply
    // wasn't part of this combat (or whose mirror was momentarily stale for
    // an unrelated reason, e.g. a dropped/failed earlier PLAY_CARD mirror)
    // would be silently "missing" from that snapshot too, and got wrongly
    // destroyed even though it was never attacked. Only ever trust an
    // explicit "this uid was destroyed" from the attacker.
    HP_RECONCILE({ units, destroyedUids }) {
      // Apply authoritative HP and damaged flag from the attacker's perspective.
      [...state.playerField, ...state.oppField].forEach(c => {
        if (!c) return;
        const sync = units.find(u => u.card.uid === c.uid);
        if (sync) { c.hp = sync.card.hp; c.damaged = sync.card.damaged; }
      });
      // Resurrect any unit the attacker's authoritative combat resolution
      // says survived (listed here, NOT in destroyedUids) but that OUR OWN
      // independent runDamageStep replay already destroyed locally before
      // this reconciliation arrived — the ATTACK message and this follow-up
      // HP_RECONCILE are sent back-to-back, but our own local combat replay
      // (triggered by the ATTACK message) runs synchronously and can finish
      // — including destroying a unit — before the correction's network
      // round trip completes. `mine` is from the ATTACKER's point of view,
      // so on THIS (receiving) client `mine: true` means it belongs on our
      // oppField and `mine: false` means it's actually ours (playerField).
      // Trigger-effect side effects from the premature destruction (e.g. a
      // [Destroyed] ability that already resolved) can't be undone, but the
      // field state itself — which is what "is this unit alive" actually
      // depends on — is corrected so the two clients stop permanently
      // disagreeing about it.
      const stillPresent = new Set([...state.playerField, ...state.oppField].filter(Boolean).map(c => c.uid));
      const destroyedSet = new Set(destroyedUids || []);
      units.forEach(({ card, slot, mine }) => {
        if (stillPresent.has(card.uid) || destroyedSet.has(card.uid)) return;
        const targetField = mine ? state.oppField : state.playerField;
        const restoreSlot = !targetField[slot] ? slot : targetField.findIndex(c => !c);
        if (restoreSlot === -1) {
          console.warn(`[HP_RECONCILE] can't resurrect ${card.name} (${card.uid}) — no empty slot`);
          return;
        }
        targetField[restoreSlot] = card;
        // Undo the trash bookkeeping our premature destruction added, for
        // both the unit and any pilot it had paired — same uid either way.
        [state.playerTrash, state.oppTrash].forEach((trash, i) => {
          const countKey = i === 0 ? 'trashCount' : 'oppTrashCount';
          [card.uid, card.pilot?.uid].filter(Boolean).forEach(uid => {
            const idx = trash.findIndex(c => c.uid === uid);
            if (idx !== -1) { trash.splice(idx, 1); state[countKey] = Math.max(0, state[countKey] - 1); }
          });
        });
        addLog(`${card.name} was still alive — corrected after network reconciliation.`);
      });
      let corrected = 0;
      (destroyedUids || []).forEach(uid => {
        const i = state.playerField.findIndex(c => c && c.uid === uid);
        if (i === -1) return;
        const c = state.playerField[i];
        state.playerField[i] = null;
        const isToken = isUnitToken(c);
        if (!isToken) {
          updateTempState(c.cardNo, c.copyVer, 'trash');
          state.trashCount++;
          state.playerTrash.push(toTrashCard(c));
        }
        if (c.pilot) {
          updateTempState(c.pilot.cardNo, c.pilot.copyVer, 'trash');
          state.trashCount++;
          state.playerTrash.push(toTrashCard(c.pilot));
        }
        addLog(`${c.name} was destroyed in combat.`);
        triggerCardEffect(c, 'destroyed', { ownerSide: 'player' });
        if (c.pilot) triggerCardEffect(c.pilot, 'destroyed', { ownerSide: 'player', unit: c });
        corrected++;
      });
      if (corrected > 0) broadcastCounters();
      render();
      setupDropZones();
    },
    // Post-combat pilot re-sync sent by BOTH clients after every battle (see
    // runEpilogue in game.js) — each side is the sole authority on its own
    // units' pilot pairing, so this heals any stale/missing .pilot on our
    // oppField mirror of the sender's units (e.g. a paired pilot's HP/AP
    // bonus silently dropping out of a card's effective-stat display).
    FIELD_PILOT_RESYNC({ units }) {
      units.forEach(({ uid, pilot }) => {
        const c = state.oppField.find(f => f && f.uid === uid);
        if (c) c.pilot = pilot;
      });
      render();
    },
    // Post-battle field consistency check, sent by BOTH clients after every
    // battle (see runEpilogue in game.js) regardless of attacker/defender
    // role — `uids` is the sender's own real, authoritative playerField.
    // Compares it against OUR mirror of the sender (oppField): whichever
    // side has fewer cards wins — if our mirror has MORE entries than the
    // sender's real list, those extras are "lingering" cards the sender's
    // own field says don't exist, and get removed. Deliberately does NOT
    // add anything back when our mirror has FEWER entries than the sender's
    // real list — this handler only ever trims its own (non-authoritative)
    // mirror; it never touches state.playerField and never tells the sender
    // to delete anything from THEIR authoritative field, so it can't be the
    // mechanism that erases a real, still-alive card. That opposite
    // direction (mirror missing a card the real owner still has) is what
    // HP_RECONCILE's resurrect branch above already exists to fix.
    FIELD_SYNC_CHECK({ uids }) {
      const realSet = new Set(uids || []);
      const mirrorCount = state.oppField.filter(Boolean).length;
      if (mirrorCount <= realSet.size) return; // mirror already <= real — nothing to trim
      let trimmed = 0;
      state.oppField.forEach((c, i) => {
        if (c && !realSet.has(c.uid)) {
          addLog(`${c.name} — lingering card cleared from opponent's field (post-battle sync check).`);
          state.oppField[i] = null;
          trimmed++;
        }
      });
      if (trimmed > 0) { render(); setupDropZones(); }
    },
    CARD_TOAST({ msg, imgPath }) {
      showCardToast(msg, imgPath);
    },
    EFFECT_ACTIVATED_TOAST({ actorName, keyword }) {
      showEffectActivatedToast(actorName, keyword);
    },
    // See broadcastLog() in game.js — mirrors a log line the sender already
    // added to their own log, for events that are public knowledge (self-
    // discards, effect damage, destroys) but only the acting client's own
    // code path ever computes. currentLogActor is already 'opp' here (see
    // the shared message listener above), so this picks up the right pill.
    LOG_MIRROR({ msg, opts }) {
      addLog(msg, opts);
    },
  };

  // .selected is a local-only "which card am I about to attack with" UI flag
  // (see game.js's onCardClick) that lives directly on card objects — strip it
  // before a card crosses the wire so the peer's oppField copy never reveals
  // which of our own cards we currently have selected.
  function stripSelected(card) {
    if (!card) return card;
    const { selected, ...rest } = card;
    if (rest.pilot) rest.pilot = stripSelected(rest.pilot);
    return rest;
  }

  function send(msg) {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(msg));
    }
  }

  // Same-origin by default (works both locally via `npm start` and once
  // deployed, since server/relay.js serves the page and the WS relay from
  // the same host/port). A ?relay=wss://host/ws query param overrides this,
  // e.g. for pointing a locally-opened copy of the game at a remote relay.
  // mode, when given, is appended as ?mode=... once the base URL is fully
  // resolved (override / file: / same-origin) so it composes correctly
  // regardless of which branch ran — e.g. a ?relay= override with a query
  // string of its own still gets &mode=lobby, not a second leading '?'.
  // Called with no argument (every existing call site), this is 100%
  // unchanged: no mode means no query string means the server sees nothing
  // and takes its default (public-queue) branch.
  function relayUrl(mode) {
    const override = new URLSearchParams(location.search).get('relay');
    let url;
    if (override) {
      url = override;
    } else if (location.protocol === 'file:') {
      // index.html opened directly (file://) rather than served by
      // server/relay.js — location.host is empty, so same-origin can't work.
      // Fall back to the old local-dev default of a relay on localhost:8080.
      url = 'ws://localhost:8080/ws';
    } else {
      const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
      url = `${scheme}//${location.host}/ws`;
    }
    if (mode) url += (url.includes('?') ? '&' : '?') + 'mode=' + encodeURIComponent(mode);
    // Sent on every connection (not just lobby ones) so server/relay.js can
    // attach it to the socket before any match is made — the public queue
    // can pair two sockets the instant they connect, with no chance to ask
    // for a username afterward, so this is the only way it can ever learn
    // one. game.js's getEffectiveUsername() is a plain global function
    // defined by the time a player actually triggers a connect (network.js
    // itself only loads before game.js, it doesn't call into it at load
    // time), so it's safe to call here. Always returns something non-empty
    // (a saved username, else a random "EFSF-AB001"-style callsign), unlike
    // the old loadProfileData().username check this replaced, which skipped
    // sending anything at all for a player who'd never set one.
    const uname = (typeof getEffectiveUsername === 'function' ? getEffectiveUsername() : '').trim();
    if (uname) url += (url.includes('?') ? '&' : '?') + 'username=' + encodeURIComponent(uname);
    return url;
  }

  // opts.mode: pass 'lobby' to connect for Private Games instead of the
  // default public matchmaking queue (see server/relay.js's ?mode=lobby
  // gating) — every existing call site passes nothing and is unaffected.
  function connect(opts = {}) {
    setBadge('Not connected');
    intentionalDisconnect = false;
    const url = relayUrl(opts.mode);
    console.log(`[network] connecting to ${url} …`);
    try {
      socket = new WebSocket(url);
    } catch (err) {
      console.warn('[network] failed to open socket', err);
      return;
    }

    socket.addEventListener('open', () => {
      console.log('[network] socket open — waiting for ROLE/MATCH_READY');
      setBadge('Waiting for opponent…');
    });

    socket.addEventListener('message', (event) => {
      let msg;
      try { msg = JSON.parse(event.data); } catch (err) {
        console.warn('[network] failed to parse message', event.data, err);
        return;
      }

      console.log('[network] received', msg.type, msg);

      if (msg.type === 'ROLE') {
        window.NET.role = msg.role;
        // '' means the opponent has no username set (or sent none) — callers
        // (game.js's actorDisplayName) fall back to the "Player 1"/"Player 2"
        // seat label in that case, same as for an unset local username.
        window.NET.oppUsername = typeof msg.oppUsername === 'string' ? msg.oppUsername : '';
        setBadge(`Connected — ${msg.role === 'player1' ? 'Player 1' : 'Player 2'}`);
        return;
      }

      const handler = remoteHandlers[msg.type];
      if (handler) {
        // Every addLog() call triggered synchronously by an opponent-originated
        // message (directly, or via the hooks.xxx callbacks these handlers
        // invoke) should be attributed to the opponent — see currentLogActor
        // in game.js.
        const prevActor = currentLogActor;
        currentLogActor = 'opp';
        try {
          handler(msg);
        } finally {
          currentLogActor = prevActor;
        }
      } else {
        console.warn('[network] no handler for message type', msg.type);
      }
    });

    socket.addEventListener('close', (event) => {
      console.log(`[network] socket closed (code=${event.code} reason="${event.reason}")`);
      setBadge('Not connected');
      if (!intentionalDisconnect) hooks.socketClosedUnexpectedly.forEach(cb => cb(event));
      intentionalDisconnect = false;
    });
    socket.addEventListener('error', (event) => {
      // Connection failure must never block or alter local-only play —
      // but log it so a missing/crashed relay is visible in the console,
      // and let game.js know so a match in progress doesn't wait forever.
      console.warn('[network] socket error', event);
      hooks.socketError.forEach(cb => cb(event));
    });
  }

  function disconnect() {
    if (socket) {
      console.log('[network] disconnecting');
      intentionalDisconnect = true;
      socket.close();
      socket = null;
    }
    matchReadyFired = false;
    hooks.matchReady = [];
    // Lobby hooks reset the same way matchReady does — the Private Games
    // page can be entered/left/re-entered multiple times in one session,
    // and without this a second visit's registration would stack alongside
    // the first visit's now-stale one and double-fire every lobby event.
    // socketError/socketClosedUnexpectedly are deliberately NOT reset here
    // (see their permanent, once-ever registration in game.js) — clearing
    // those would silently break disconnect handling for the rest of the
    // page's life after the very first disconnect ever happens.
    hooks.lobbyCreated = [];
    hooks.lobbyJoined = [];
    hooks.lobbyState = [];
    hooks.lobbyError = [];
    hooks.challengeReceived = [];
    hooks.challengeSent = [];
    hooks.challengeDeclined = [];
    hooks.challengeTimeout = [];
    hooks.challengeAccepted = [];
    hooks.matchAborted = [];
    hooks.lobbyClosed = [];
    hooks.lobbyKicked = [];
    window.NET.role = null;
    window.NET.oppUsername = '';
  }

  window.NET = {
    role: null,
    oppUsername: '',
    send,
    // Deliberately NOT called automatically on page load — connecting (and
    // thus taking a relay seat) only makes sense once the player has picked
    // a deck and actually wants to queue for a match.
    connect,
    disconnect,
    onMatchReady(cb) {
      if (matchReadyFired) {
        console.log('[network] onMatchReady registered after the fact — firing immediately');
        cb();
      } else {
        hooks.matchReady.push(cb);
      }
    },
    onOpponentLeft(cb) { hooks.opponentLeft.push(cb); },
    onSocketError(cb) { hooks.socketError.push(cb); },
    onSocketClosedUnexpectedly(cb) { hooks.socketClosedUnexpectedly.push(cb); },
    // Private Games lobby — see server/relay.js's LOBBY_* messages.
    onLobbyCreated(cb) { hooks.lobbyCreated.push(cb); },
    onLobbyJoined(cb) { hooks.lobbyJoined.push(cb); },
    onLobbyState(cb) { hooks.lobbyState.push(cb); },
    onLobbyError(cb) { hooks.lobbyError.push(cb); },
    onChallengeReceived(cb) { hooks.challengeReceived.push(cb); },
    onChallengeSent(cb) { hooks.challengeSent.push(cb); },
    onChallengeDeclined(cb) { hooks.challengeDeclined.push(cb); },
    onChallengeTimeout(cb) { hooks.challengeTimeout.push(cb); },
    onChallengeAccepted(cb) { hooks.challengeAccepted.push(cb); },
    onMatchAborted(cb) { hooks.matchAborted.push(cb); },
    onLobbyClosed(cb) { hooks.lobbyClosed.push(cb); },
    onLobbyKicked(cb) { hooks.lobbyKicked.push(cb); },
    onBo3SideboardReady(cb) { hooks.bo3SideboardReady.push(cb); },
    onBo3TurnChoice(cb) { hooks.bo3TurnChoice.push(cb); },
    onMulliganDone(cb) { hooks.mulliganDone.push(cb); },
    onPhaseSync(cb) { hooks.phaseSync.push(cb); },
    onEndTurn(cb) { hooks.endTurn.push(cb); },
    onSurrender(cb) { hooks.surrender.push(cb); },
    onCounterSync(cb) { hooks.counterSync.push(cb); },
    onShieldReveal(cb) { hooks.shieldReveal.push(cb); },
    onShieldResolve(cb) { hooks.shieldResolve.push(cb); },
    onAttackBlock(cb) { hooks.attackBlock.push(cb); },
    onAttackAction(cb) { hooks.attackAction.push(cb); },
    onUnitBounce(cb) { hooks.unitBounce.push(cb); },
    onUnitToDeckBottom(cb) { hooks.unitToDeckBottom.push(cb); },
    onForceShieldHit(cb) { hooks.forceShieldHit.push(cb); },
    onForceDestroyNonBattling(cb) { hooks.forceDestroyNonBattling.push(cb); },
    onOfferDiscard(cb) { hooks.offerDiscard.push(cb); },
    onDiscardResponse(cb) { hooks.discardResponse.push(cb); },
    onOfferDraw(cb) { hooks.offerDraw.push(cb); },
    onDrawResponse(cb) { hooks.drawResponse.push(cb); },
    onForceDiscard(cb) { hooks.forceDiscard.push(cb); },
    onSpecialMoveCommandActivated(cb) { hooks.specialMoveCommandActivated.push(cb); },
    onFieldApMod(cb) { hooks.fieldApMod.push(cb); },
    onUnitRest(cb) { hooks.unitRest.push(cb); },
    onDestroyedEffectDone(cb) { hooks.destroyedEffectDone.push(cb); },
    onAttackTriggersDone(cb) { hooks.attackTriggersDone.push(cb); },
    onStuckCheck(cb) { hooks.stuckCheck.push(cb); },
    onStuckCheckResponse(cb) { hooks.stuckCheckResponse.push(cb); },
    onRequestMutualBattleUnit(cb) { hooks.requestMutualBattleUnit.push(cb); },
    onMutualBattleUnitResponse(cb) { hooks.mutualBattleUnitResponse.push(cb); },
    onRequestTrashUnitExile(cb) { hooks.requestTrashUnitExile.push(cb); },
    onTrashUnitExileDone(cb) { hooks.trashUnitExileDone.push(cb); },
    onEndPhaseOppRequest(cb) { hooks.endPhaseOppRequest.push(cb); },
    onEndPhaseAction(cb) { hooks.endPhaseAction.push(cb); },
    onCardReveal(cb) { hooks.cardReveal.push(cb); },
    onDeckMill(cb) { hooks.deckMill.push(cb); },
  };

  // network.js loads before game.js (so window.NET exists for game.js's
  // top-level NET.on* registrations), but setActionListener is defined by
  // game.js — so this part waits until the document (and thus both
  // synchronous scripts) has finished parsing and executing.
  document.addEventListener('DOMContentLoaded', () => {
    // Mirrors local actions to the peer. Runs after the local handler has
    // already applied the action to state, so the full card object can be
    // read straight out of state instead of being reconstructed from the
    // (intentionally minimal) dispatch payload.
    setActionListener((type, payload) => {
      if (type === 'PLAY_CARD' || type === 'PAIR_PILOT') {
        send({ type: 'PLAY_CARD', slot: payload.slot, card: stripSelected(state.playerField[payload.slot]), counters: myCounters() });
      } else if (type === 'ATTACK' && payload.mine !== false) {
        send({
          type: 'ATTACK',
          attackerUid: payload.attackerUid,
          defenderUid: payload.defenderUid,
          isShield: payload.isShield,
          isDirect: payload.isDirect,
          // skipToDamage: e.g. GD05-017 Nu Gundam's "begin a battle, skipping
          // straight to the damage step" — both clients need to agree to take
          // the shortcut path in registerAction('ATTACK', ...).
          skipToDamage: payload.skipToDamage,
        });
      } else if (type === 'ACTIVATE_BASE') {
        send({ type: 'ACTIVATE_BASE', counters: myCounters(), rested: state.playerBase ? state.playerBase.rested : false });
      } else if (type === 'ACTIVATE_COMMAND') {
        send({ type: 'ACTIVATE_COMMAND', card: payload.card, counters: myCounters() });
      } else if (type === 'CLEAR_TURN_EFFECTS') {
        send({ type: 'CLEAR_TURN_EFFECTS' });
      } else if (type === 'NEW_TURN') {
        // Mirrors the "Turn N begins" marker itself — startNewTurn() (and the
        // dispatch('NEW_TURN') inside it) only ever runs on whichever client's
        // turn is actually starting (triggered by NET.onEndTurn below), so
        // without this the OTHER client's log silently had no line at all for
        // any turn that wasn't their own. Log-only: the receiving side's own
        // turn/phase state is already kept in sync separately (PHASE_SYNC,
        // END_TURN), so this never touches state.turn itself.
        send({ type: 'NEW_TURN', turn: state.turn });
      } else if (type === 'UNREST_ALL') {
        send({ type: 'UNREST_ALL' });
      } else if (type === 'SWAP_SLOTS') {
        send({ type: 'SWAP_SLOTS', srcSlot: payload.srcSlot, tgtSlot: payload.tgtSlot });
      } else if (type === 'DISCARD_CARD' && payload.source === 'field') {
        send({ type: 'DISCARD_CARD', slot: payload.slot });
      }
    });
  });
})();
